import { ActionIcon, Button, CopyButton, Group, Menu, Stack, Text, TextInput } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCopy, IconCopyCheck } from "@tabler/icons-react";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { Content } from "shared/components/global/Content";
import { FloatingActionButton } from "shared/components/global/FloatingActionButton/FloatingActionButton";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { IpawsAlertCard } from "shared/components/ipaws/alert/IpawsAlertCard";
import { useIpawsAlertActions } from "shared/components/ipaws/alert/useIpawsAlertActions";
import { permissionCheck, usePermissionCheck, usePermissions } from "shared/stores/oidc";
import { titlecase, to } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { qk } from "shared/utils/qk";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { zx } from "shared/utils/zx";
import { NoMessagesFigure } from "src/assets/NoMessagesFigure";
import { graphql } from "src/gql";
import { IpawsAlertsQuery, IpawsAlertStatus, Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { match } from "ts-pattern";

const NoMessageText = () => {
    const hasCreatePermission = usePermissionCheck(Permission.IpawsAlertModify);
    if (hasCreatePermission)
        return (
            <Text ta={"center"}>
                Looks like you haven't broadcast any IPAWS alerts yet, you can&nbsp;
                <Link to="/ipaws/alerts/new">
                    <Text span c="brand">
                        click here
                    </Text>
                </Link>
                &nbsp;to get started!
            </Text>
        );
    return <Text ta={"center"}>Looks like you haven't broadcast any IPAWS alerts yet.</Text>;
};

function openViewAlertIdModal(alert: IpawsAlertsQuery["ipawsAlerts"][number]) {
    modals.open({
        title: "IPAWS Alert ID",
        children: (
            <Stack>
                <TextInput
                    variant="filled"
                    readOnly
                    value={alert.capMessage.identifier}
                    rightSection={
                        <CopyButton value={alert.capMessage.identifier}>
                            {({ copied, copy }) => (
                                <ActionIcon variant="transparent" onClick={copy}>
                                    {copied ? <IconCopyCheck size={16} /> : <IconCopy size={16} />}
                                </ActionIcon>
                            )}
                        </CopyButton>
                    }
                />
                <Group justify="flex-end">
                    <Button variant="default" onClick={modals.closeAll}>
                        Close
                    </Button>
                </Group>
            </Stack>
        ),
    });
}

const Page = () => {
    const { permissions } = usePermissions();
    const { data } = useSuspenseInfiniteQuery(queries.alerts);
    const alerts = data.pages.flat();
    const canViewAlerts = usePermissionCheck(Permission.IpawsHistoryView);
    const hasIpawsCreate = usePermissionCheck(Permission.IpawsAlertModify);
    const nav = useNavigate();
    const actions = useIpawsAlertActions();

    function canCancelAlert(certId: string) {
        return permissionCheck(permissions, Permission.IpawsMessageCancel, certId);
    }

    if (!canViewAlerts) return <Navigate to="/ipaws" />;

    return (
        <Content>
            <Content.Heading>IPAWS Alerts</Content.Heading>

            {alerts.length == 0 && (
                <NoMessagesFigure>
                    <NoMessageText />
                </NoMessagesFigure>
            )}

            <Stack>
                {alerts.map((each) => {
                    const enInfo = each.capMessage.infos.at(0);
                    const expired = maybe(enInfo)?.take((it) => new Date() >= zx.date().parse(it.expires)) ?? true;
                    const cancelled = each.status == IpawsAlertStatus.Cancel;
                    return (
                        <IpawsAlertCard data={each} key={each.id}>
                            <IpawsAlertCard.PrimaryAction
                                onClick={() => void nav({ to: "/ipaws/alerts/$alertId", params: { alertId: each.id } })}
                                disabled={!enInfo}
                            >
                                View
                            </IpawsAlertCard.PrimaryAction>
                            <IpawsAlertCard.Action
                                disabled={!canCancelAlert(each.certificate.id) || cancelled || expired || !enInfo}
                                onClick={() => void actions.openCancelModal({ alertId: each.id })}
                            >
                                Cancel
                            </IpawsAlertCard.Action>
                            <IpawsAlertCard.Action
                                onClick={() => void nav({ to: "/ipaws/update/$alertId", params: { alertId: each.id } })}
                                disabled={cancelled || expired || !enInfo}
                            >
                                Update
                            </IpawsAlertCard.Action>
                            <IpawsAlertCard.Action onClick={() => openViewAlertIdModal(each)}>View Alert ID</IpawsAlertCard.Action>

                            {each.status !== IpawsAlertStatus.Published && (
                                <IpawsAlertCard.Badge
                                    color={match(each.status)
                                        .with(IpawsAlertStatus.Cancel, () => "blue")
                                        .otherwise(() => "gray")}
                                >
                                    {titlecase(each.status)}
                                </IpawsAlertCard.Badge>
                            )}

                            {each.status != IpawsAlertStatus.Cancel && !expired ? (
                                <IpawsAlertCard.Badge color="green">Active</IpawsAlertCard.Badge>
                            ) : (
                                <IpawsAlertCard.Badge color="red">Expired</IpawsAlertCard.Badge>
                            )}
                        </IpawsAlertCard>
                    );
                })}
            </Stack>

            <LoadMoreTrigger query={queries.alerts} />

            {hasIpawsCreate && (
                <FloatingActionButton>
                    <Menu.Item component={Link} to="/ipaws/alerts/new">
                        New IPAWS Alert
                    </Menu.Item>
                </FloatingActionButton>
            )}
        </Content>
    );
};

const queries = {
    alerts: createOffsetBasedGqlQueryOptions({
        queryKey: qk("ipaws", "alerts"),
        document: graphql(`
            query IpawsAlerts($offset: Int!, $limit: Int!) {
                ipawsAlerts(status: [PUBLISHED, UPDATE, CANCEL], limit: $limit, offset: $offset) {
                    ...IpawsAlertThumbnail
                }
            }
        `),
        select: to("ipawsAlerts"),
    }),
};

/** @public */
export const Route = createFileRoute("/_auth/ipaws/alerts/")({
    component: Page,
    async beforeLoad() {
        await queryClient.prefetchInfiniteQuery(queries.alerts);
    },
});
